import Utils from 'app/common/util/Utils';
import { Key } from 'react';
import { Filter } from 'app/common/table/Filter';
import { ProvidesKey } from 'app/model/client/CORE';
import { Highlighter } from 'app/common/table/Highlighter';
import ManagedTable, { ManagedColumn, ManagedTableSorting } from 'app/common/table/ManagedTable';

type Comparator = (o1: any, o2: any) => boolean;

export class TableUtils {
    /**
     * Sort the given data, using the table-defined columns & sorting context.
     * This function should give the same result as an 'internal' ManagedTable sort.
     *
     * The function will return the sorted data.
     * @param data
     * @param columns
     * @param sortings
     * @param table (optional) - to provide if your column render methods make use of the 2nd parameter (table)
     * @param priorityComparator (optional) - to provide when need to add personalised priority when sorting
     */
    static sortData<T>(
        data: Array<T>,
        columns: Array<ManagedColumn<T>>,
        sortings: Array<ManagedTableSorting>,
        table?: ManagedTable<T, any>,
        priorityComparator?: (a: T, b: T) => number,
    ): Array<T> {
        if (!sortings || sortings.length === 0) return data;
        if (!data) return data;

        const sortColumns = sortings.map(sorting => columns.find(col => col.header === sorting.columnName));
        const copyData = [...data];
        copyData.sort((d1: T, d2: T) => {
            for (let i = 0; i < sortings.length; i++) {
                const dir = sortings[i].direction;
                const col = sortColumns[i];
                if (col) {
                    let result = 0;
                    if (priorityComparator) {
                        result = priorityComparator(d1, d2);
                        if (result !== 0) return result;
                    }

                    const render1 = col.getCellValue(dir === 'asc' ? d1 : d2, table);
                    const render2 = col.getCellValue(dir === 'asc' ? d2 : d1, table);

                    if (col.extra?.comparator) {
                        result = col.extra.comparator(render1, render2);
                    } else {
                        // default compare
                        if (render1 > render2) {
                            result = 1;
                        } else if (render1 < render2) {
                            result = -1;
                        }
                    }
                    if (result !== 0) return result;
                }
            }
            return 0;
        });
        return copyData;
    }

    static toWidthPx(width: any, fontSizePx?: number): any {
        if (typeof width === 'number') return width;

        if (typeof width === 'string') {
            const widthStr: string = width;
            if (widthStr.endsWith('px')) {
                return parseInt(widthStr.substring(0, widthStr.indexOf('px')));
            } else if (widthStr.endsWith('em')) {
                return parseInt(widthStr.substring(0, widthStr.indexOf('em'))) * fontSizePx;
            }
        }
        return width;
    }

    /**
     * This utility method is useful to get the new selected element from previous selection, after new data has been queried.
     * The complexity of this algorithm is quite high, since it is possibly performing deep comparison of elements,
     * so it should only be used when necessary, typically when new data is received.
     * @param selection
     * @param data
     * @param keyProperty
     */
    static getUpdatedSingleSelection<T extends object>(
        selection: T,
        data: Array<T>,
        keyProvider?: ProvidesKey<T>,
        comparator?: Comparator,
    ): T {
        if (selection) {
            const newSelection: T[] = TableUtils.getUpdatedMultipleSelection(
                [selection],
                data,
                keyProvider,
                comparator,
            );
            if (newSelection.length > 0) return newSelection[0];
        }
        return undefined;
    }

    /**
     * This utility method is useful to get the new selected elements from previous selection, after new data has been queried.
     * The complexity of this algorithm is quite high, since it is possibly performing deep comparison of elements,
     * so it should only be used when necessary, typically when new data is received.
     * @param selection
     * @param data
     * @param keyProvider
     * @param comparator
     */
    static getUpdatedMultipleSelection<T extends object>(
        selection: Array<T>,
        data: Array<T>,
        keyProvider?: ProvidesKey<T>,
        comparator: Comparator = Utils.isEquals,
    ): T[] {
        if (!selection) {
            return []; // this function has always returned a 'defined' value.. so I don't want to change the semantics now, otherwise we need to check 100s of calls.
        } else if (selection.length === 0) {
            return selection; // to avoid PURE Components re-rendering
        } else if (!data) {
            return [];
        } else {
            const selectionCopy = [...selection]; // Work on a copy, so that found values are removed when index is found, in order to decrease complexity of the search algorithm
            const newSelection = new Array<T>();
            for (let i = 0; i < data.length && selectionCopy.length > 0; i++) {
                const el_i = data[i];
                const key_i = keyProvider.getKey(el_i);
                for (let j = 0; j < selectionCopy.length; j++) {
                    const object = selectionCopy[j];
                    if (comparator(key_i, keyProvider.getKey(object))) {
                        newSelection.push(data[i]);
                        selectionCopy.splice(j, 1); // remove element to be searched from the list, we already found it
                        break;
                    }
                }
            }
            return newSelection;
        }
    }

    static getMissingSelectionOnData<T extends object>(
        selection: Array<T>,
        data: Array<T>,
        keyProvider?: ProvidesKey<T>,
        comparator?: Comparator,
    ): T[] {
        if (selection) {
            const compareFn = comparator || Utils.isEquals;
            const selectionCopy = [...selection]; // Work on a copy, so that found values are removed when index is found, in order to decrease complexity of the search algorithm
            const newSelection = new Array<T>();
            for (let i = 0; i < data.length && selectionCopy.length > 0; i++) {
                for (let j = 0; j < selectionCopy.length; j++) {
                    const object = selectionCopy[j];
                    if (
                        (keyProvider && compareFn(keyProvider.getKey(data[i]), keyProvider.getKey(object))) ||
                        (!keyProvider && compareFn(data[i], object))
                    ) {
                        newSelection.push(data[i]);
                        selectionCopy.splice(j, 1); // remove element to be searched from the list, we already found it
                        break;
                    }
                }
            }
            return selectionCopy;
        }
        return [];
    }

    /**
     * This utility method calculates the row keys of a table based on data/selection.
     * @param data
     * @param selection
     * @param selectionFilterFunction
     */
    public static rowKeysFromSelection<T>(
        data: T[],
        selection: T | T[],
        selectionFilterFunction?: (element: T, selection: T | T[]) => boolean,
    ): Key[] {
        if (selection && data) {
            // Getting rid of potential -1 values returned by indexOf
            if (Array.isArray(selection)) {
                let result: number[] = [];
                if (selectionFilterFunction) {
                    for (let idx = 0; idx < data.length; idx++) {
                        if (selectionFilterFunction(data[idx], selection)) {
                            result.push(idx);
                        }
                    }
                } else {
                    result = selection.map((item: T) => {
                        return data.indexOf(item);
                    });
                }
                result = result.filter(idx => {
                    return idx >= 0;
                });
                return result;
            } else {
                if (selectionFilterFunction) {
                    for (let idx = 0; idx < data.length; idx++) {
                        if (selectionFilterFunction(data[idx], selection)) {
                            return [idx].filter(idx => {
                                return idx >= 0;
                            });
                        }
                    }
                } else {
                    return [data.indexOf(selection)].filter(idx => {
                        return idx >= 0;
                    });
                }
            }
        }
        return [];
    }

    /**
     * Return those data elements that would be kept by this filter.
     * @param data
     * @param filter
     */
    static getFilteredData<T>(data: T[], filter: Filter<T>): T[] {
        if (filter && data) {
            const filteredData = new Array<T>();
            for (const elem of data) {
                if (filter.keep(elem)) {
                    filteredData.push(elem);
                }
            }
            return filteredData;
        }
        return data;
    }

    /**
     * Return those data elements that would be highlighted
     * @param data
     * @param highlighter
     */
    static getHighlightedData<T>(data: T[], highlighter: Highlighter<T>): T[] {
        if (highlighter && data) {
            const reply = new Array<T>();
            for (const elem of data) {
                if (highlighter.highlight(elem)) {
                    reply.push(elem);
                }
            }
            return reply;
        }
        return data;
    }

    /**
     * Return the number of data elements that would be highlighted by this highlighter
     * @param data
     * @param highlighter
     */
    static getNumHighlighted<T>(data: T[], highlighter: Highlighter<T>): number {
        return highlighter && data
            ? data.reduce((accum, curr) => (highlighter.highlight(curr) ? accum + 1 : accum), 0)
            : 0;
    }

    /**
     * Numerous tables include an information line, indicating the number of rows, visible rows, highlighted rows.
     * This utility method returns a standardized string
     * @param numTotal
     * @param numVisible
     * @param numHighlighted
     * @param dataType
     */
    static toTableInfoString(numTotal: number, numVisible: number, numHighlighted: number, dataType: string) {
        if (numVisible === numTotal) {
            if (numHighlighted === 0) {
                return numTotal + ' ' + dataType;
            } else {
                return numHighlighted + ' of ' + numTotal + ' ' + dataType;
            }
        } else {
            if (numHighlighted === 0) {
                return numVisible + ' of ' + numTotal + ' ' + dataType;
            } else {
                return numHighlighted + '/' + numVisible + ' of ' + numTotal + ' ' + dataType;
            }
        }
    }
}
